import createEmotion, {
  type CSSInterpolation,
} from "@emotion/css/create-instance";
import { useInsertionEffect, useMemo } from "react";

import { createArkTokens, arkThemeVariables } from "./ArkThemeTokens";

// A single compiler/cache per document also serves separately built first-party apps.
// It only compiles CSS; theme values and component styling are owned by Ark.
const cacheKey = Symbol.for("ark.styles.runtime.v1");
type StyleRuntime = ReturnType<typeof createEmotion>;
type SharedStyleWindow = Window & { [cacheKey]?: StyleRuntime };
function styleRuntime(): StyleRuntime {
  if (typeof window === "undefined") return createEmotion({ key: "ark" });
  const target = window as SharedStyleWindow;
  return (target[cacheKey] ??= createEmotion({ key: "ark" }));
}
const runtime = styleRuntime();
const compileCSS = runtime.css.bind(runtime);
const combineClasses = runtime.cx.bind(runtime);

const dimensionTokens = createArkTokens("light");
const semanticVariable = (key: keyof typeof arkThemeVariables) =>
  `var(${arkThemeVariables[key]})`;
const toneBackground = (name: "success" | "warning" | "danger" | "info") =>
  `color-mix(in srgb, ${semanticVariable(name)} 12%, ${semanticVariable("surface")})`;
const toneBorder = (name: "success" | "warning" | "danger" | "info") =>
  `color-mix(in srgb, ${semanticVariable(name)} 35%, ${semanticVariable("border")})`;

// Transitional names let existing page CSS migrate without retaining a library
// provider or its derived palette. New CSS should reference --ark-* directly.
export const arkStyleTokens = Object.freeze({
  borderRadius: dimensionTokens.radius,
  borderRadiusLG: dimensionTokens.radiusLarge,
  borderRadiusSM: dimensionTokens.radiusSmall,
  colorBgBase: semanticVariable("canvas"),
  colorBgContainer: semanticVariable("surface"),
  colorBgElevated: semanticVariable("surfaceElevated"),
  colorBgLayout: semanticVariable("canvas"),
  colorBorder: semanticVariable("border"),
  colorBorderSecondary: semanticVariable("divider"),
  colorFill: semanticVariable("fillSecondary"),
  colorFillAlter: semanticVariable("fill"),
  colorFillQuaternary: semanticVariable("fill"),
  colorFillSecondary: semanticVariable("fillSecondary"),
  colorFillTertiary: semanticVariable("fill"),
  colorText: semanticVariable("text"),
  colorTextSecondary: semanticVariable("textSecondary"),
  colorTextTertiary: semanticVariable("textTertiary"),
  colorTextQuaternary: semanticVariable("textDisabled"),
  colorTextLightSolid: semanticVariable("textInverse"),
  colorPrimary: semanticVariable("primary"),
  colorPrimaryActive: semanticVariable("primaryActive"),
  colorPrimaryHover: semanticVariable("primaryHover"),
  colorPrimaryBg: semanticVariable("primarySoft"),
  colorPrimaryBgHover: semanticVariable("primarySoftHover"),
  colorPrimaryBorder: toneBorder("info"),
  colorPrimaryText: semanticVariable("primary"),
  colorLink: semanticVariable("primary"),
  colorSuccess: semanticVariable("success"),
  colorSuccessBg: toneBackground("success"),
  colorSuccessBorder: toneBorder("success"),
  colorSuccessText: semanticVariable("success"),
  colorWarning: semanticVariable("warning"),
  colorWarningBg: toneBackground("warning"),
  colorWarningBorder: toneBorder("warning"),
  colorWarningText: semanticVariable("warning"),
  colorError: semanticVariable("danger"),
  colorErrorBg: toneBackground("danger"),
  colorErrorBorder: toneBorder("danger"),
  colorErrorText: semanticVariable("danger"),
  colorInfo: semanticVariable("info"),
  colorInfoBg: toneBackground("info"),
  colorInfoBorder: toneBorder("info"),
  colorInfoText: semanticVariable("info"),
  fontFamily: dimensionTokens.fontFamily,
  fontFamilyCode: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: dimensionTokens.fontSize,
  fontSizeSM: 12,
  screenXSMax: 575,
  screenSMMax: 767,
  screenMDMax: 991,
  screenLG: 992,
  screenLGMax: 1199,
  boxShadowSecondary:
    "0 12px 36px color-mix(in srgb, var(--ark-text) 12%, transparent)",
  boxShadowTertiary:
    "0 4px 16px color-mix(in srgb, var(--ark-text) 8%, transparent)",
});

type StyleContext = {
  css: StyleRuntime["css"];
  cx: StyleRuntime["cx"];
  token: typeof arkStyleTokens;
  theme: typeof arkStyleTokens;
};
const styleContext: StyleContext = {
  css: compileCSS,
  cx: combineClasses,
  token: arkStyleTokens,
  theme: arkStyleTokens,
};

// CSS variables make styles stable across theme/effect changes; no provider
// replacement or repeated style generation is needed to recolor a mounted page.
export function createStyles<Styles extends Record<string, string>>(
  factory: (context: StyleContext) => Styles,
) {
  let styles: Styles | undefined;
  return function useStyles() {
    styles ??= factory(styleContext);
    return { styles, cx: combineClasses, theme: arkStyleTokens };
  };
}

export function createGlobalStyle(
  factory: (context: Pick<StyleContext, "theme" | "token">) => CSSInterpolation,
) {
  return function ArkGlobalStyle() {
    const style = useMemo(() => factory(styleContext), []);
    useInsertionEffect(() => {
      runtime.injectGlobal(style);
    }, [style]);
    return null;
  };
}

export function useArkToken() {
  return arkStyleTokens;
}
